import { FetchState } from "../../entities/FetchState";
import { IReportDataObject, emptyReportDataObject } from "../../entities/reports/IReportData";
import { Reducer } from "redux";
import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import Either from "../../utils/Either";
import {callApiNoResult, callApiWithResult, ICallNoResult, ICallResult} from "../../utils/callApi";
import { ActionCreators as Navigation } from "../navigate"

/**********************************************************
 * API Data
 **********************************************************/
export interface IContactPerson {
    id: string;
    name: string;
    contactPersonTypeStreamId: string | null;
    contactPersonTypeName?: string;
    customTypeName: string | null;
    phoneNumber: string | null;
    emailAddress: string | null;
}

export interface IData {
    streamId: string
    uuid: string
    // Basic identity
    fullName: string
    lastName: string
    firstName: string
    burgerServiceNumber: string
    birthDate: Date | null
    gender: "Male" | "Female" | null
    isDeceased: boolean
    // Contact
    email: string | null
    homePhone: string | null
    mobilePhone: string | null
    workPhone: string | null
    // Residency / Official city info
    residencyCityId: string | null
    residencyCityName: string | null
    residencyCityPostalCode: string | null
    residencyStreet: string | null
    residencyStreetNumber: string | null
    officialCityId: string | null
    officialCityName: string | null
    officialStreet: string | null
    officialStreetNumber: string | null
    officialCityPostalCode: string | null
    // Language
    languageId: string | null
    languageName: string | null
    // Activity type
    activityTypeId: string | null
    activityTypeName: string | null
    // Disabilities / VAPH
    disabilityConfirmed: boolean | null
    disabilitySuspected: boolean | null
    disabilityTypes: string[]
    disabilityTypeIds: string[]
    problems: string[]
    problemIds: string[]
    // Other notes
    contactPerson: string | null
    contactPersons: IContactPerson[]
    remarks: string | null
}

export interface ISaveData {
    firstName: string
    lastName: string
    burgerServiceNumber: string
    dateOfBirth: string | null
    gender: "Male" | "Female" | null
    officialStreet: string
    residencyStreet: string | null
    officialStreetNumber: string
    residencyStreetNumber: string | null
    homePhone: string | null
    workPhone: string | null
    mobilePhone: string | null
    email: string | null
    contactPerson: string | null
    contactPersons: Array<{
        id: string;
        name: string;
        contactPersonTypeStreamId: string | null;
        customTypeName: string | null;
        phoneNumber: string | null;
        emailAddress: string | null;
    }>
    remarks: string | null
    disabilityStatus: "None" | "Suspected" | "Confirmed"
    languageStreamId: string | null
    residencyCityStreamId: string | null
    officialCityStreamId: string
    activityTypeStreamId: string | null
    disabilityId: string[]
    problemId: string[]
}

export const defaultClientItem: IData = {
    streamId: '',
    uuid: '',
    fullName: '',
    lastName: '',
    firstName: '',
    burgerServiceNumber: '',
    birthDate: null,
    gender: null,
    isDeceased: false,
    email: null,
    homePhone: null,
    mobilePhone: null,
    workPhone: null,
    residencyCityId: null,
    residencyCityName: null,
    residencyCityPostalCode: null,
    residencyStreet: null,
    residencyStreetNumber: null,
    officialCityId: null,
    officialCityName: null,
    officialStreet: null,
    officialStreetNumber: null,
    officialCityPostalCode: null,
    languageId: null,
    languageName: null,
    activityTypeId: null,
    activityTypeName: null,
    disabilityConfirmed: null,
    disabilitySuspected: null,
    disabilityTypes: [],
    disabilityTypeIds: [],
    problems: [],
    problemIds: [],
    contactPerson: null,
    contactPersons: [],
    remarks: null
};

export const defaultClientDetail = emptyReportDataObject<IData>(defaultClientItem)

/**********************************************************
 * State
 **********************************************************/

export interface IState {
    Item: IReportDataObject<IData>
    SaveItem: IReportDataObject<IData>
    DeleteItem: IReportDataObject<IData>
    AnonymizeItem: IReportDataObject<IData>
    MarkDeceasedItem: IReportDataObject<IData>
    UnmarkDeceasedItem: IReportDataObject<IData>
}
const InitialState: IState = {
    Item: defaultClientDetail,
    SaveItem: defaultClientDetail,
    DeleteItem: defaultClientDetail,
    AnonymizeItem: defaultClientDetail,
    MarkDeceasedItem: defaultClientDetail,
    UnmarkDeceasedItem: defaultClientDetail
}

/**********************************************************
 * Action Types
 **********************************************************/
export enum ActionTypes {
    // Fetch action types
    FetchData = "client.ClientItem Fetch",
    FetchDataSuccess = "client.ClientItem FetchSuccess",
    FetchDataFailed = "client.ClientItem FetchFailed",
    // Save action types
    SaveData = "client.ClientItem Save",
    SaveDataSuccess = "client.ClientItem SaveSuccess",
    SaveDataFailed = "client.ClientItem SaveFailed",
    // Delete action types
    DeleteData = "client.ClientItem Delete",
    DeleteDataSuccess = "client.ClientItem DeleteSuccess",
    DeleteDataFailed = "client.ClientItem DeleteFailed",
    // Anonymize action types
    AnonymizeData = "client.ClientItem Anonymize",
    AnonymizeDataSuccess = "client.ClientItem AnonymizeSuccess",
    AnonymizeDataFailed = "client.ClientItem AnonymizeFailed",
    // MarkDeceased action types
    MarkDeceasedData = "client.ClientItem MarkDeceased",
    MarkDeceasedDataSuccess = "client.ClientItem MarkDeceasedSuccess",
    MarkDeceasedDataFailed = "client.ClientItem MarkDeceasedFailed",
    // UnmarkDeceased action types
    UnmarkDeceasedData = "client.ClientItem UnmarkDeceased",
    UnmarkDeceasedDataSuccess = "client.ClientItem UnmarkDeceasedSuccess",
    UnmarkDeceasedDataFailed = "client.ClientItem UnmarkDeceasedFailed",
    // Reset action types
    ResetDeleteState = "client.ClientItem ResetDeleteState",
    ResetSaveState = "client.ClientItem ResetSaveState"
}

/**********************************************************
 * Actions & Creators
 **********************************************************/
export interface IFetchData {
    type: ActionTypes.FetchData,
    payload: { streamId: string }
}
export interface IFetchDataSuccess {
    type: ActionTypes.FetchDataSuccess,
    payload: { data: IData }
}
export interface IFetchDataFailed {
    type: ActionTypes.FetchDataFailed,
    payload: { errorMessage: string }
}
export interface ISaveDataAction {
    type: ActionTypes.SaveData,
    payload: { streamId: string, data: ISaveData }
}
export interface ISaveDataSuccess {
    type: ActionTypes.SaveDataSuccess,
    payload: { streamId: string }
}
export interface ISaveDataFailed {
    type: ActionTypes.SaveDataFailed,
    payload: { errorMessage: string }
}

// Delete Actions
export interface IDeleteData {
    type: ActionTypes.DeleteData,
    payload: { streamId: string }
}
export interface IDeleteDataSuccess {
    type: ActionTypes.DeleteDataSuccess,
    payload: { streamId: string }
}
export interface IDeleteDataFailed {
    type: ActionTypes.DeleteDataFailed,
    payload: { errorMessage: string }
}

// Anonymize Actions
export interface IAnonymizeData {
    type: ActionTypes.AnonymizeData,
    payload: { streamId: string }
}
export interface IAnonymizeDataSuccess {
    type: ActionTypes.AnonymizeDataSuccess,
    payload: { streamId: string }
}
export interface IAnonymizeDataFailed {
    type: ActionTypes.AnonymizeDataFailed,
    payload: { errorMessage: string }
}

// MarkDeceased Actions
export interface IMarkDeceasedData {
    type: ActionTypes.MarkDeceasedData,
    payload: { streamId: string }
}
export interface IMarkDeceasedDataSuccess {
    type: ActionTypes.MarkDeceasedDataSuccess,
    payload: { streamId: string }
}
export interface IMarkDeceasedDataFailed {
    type: ActionTypes.MarkDeceasedDataFailed,
    payload: { errorMessage: string }
}

// UnmarkDeceased Actions
export interface IUnmarkDeceasedData {
    type: ActionTypes.UnmarkDeceasedData,
    payload: { streamId: string }
}
export interface IUnmarkDeceasedDataSuccess {
    type: ActionTypes.UnmarkDeceasedDataSuccess,
    payload: { streamId: string }
}
export interface IUnmarkDeceasedDataFailed {
    type: ActionTypes.UnmarkDeceasedDataFailed,
    payload: { errorMessage: string }
}

// Reset Actions
export interface IResetDeleteState {
    type: ActionTypes.ResetDeleteState
}
export interface IResetSaveState {
    type: ActionTypes.ResetSaveState
}

type Actions = IFetchData | IFetchDataSuccess | IFetchDataFailed | ISaveDataAction | ISaveDataSuccess | ISaveDataFailed | IDeleteData | IDeleteDataSuccess | IDeleteDataFailed | IAnonymizeData | IAnonymizeDataSuccess | IAnonymizeDataFailed | IMarkDeceasedData | IMarkDeceasedDataSuccess | IMarkDeceasedDataFailed | IUnmarkDeceasedData | IUnmarkDeceasedDataSuccess | IUnmarkDeceasedDataFailed | IResetDeleteState | IResetSaveState;

export class ActionCreators {
    public static FetchData(streamId: string): IFetchData {
        return { 
            type: ActionTypes.FetchData, 
            payload: { streamId } 
        };
    }
    public static FetchDataSuccess(newData: IData): IFetchDataSuccess {
        return {
            type: ActionTypes.FetchDataSuccess,
            payload: { data: newData }
        };
    }
    public static FetchDataFailed(err: string): IFetchDataFailed {
        return {
            type: ActionTypes.FetchDataFailed,
            payload: { errorMessage: err }
        };
    }
    public static SaveData(streamId: string, data: ISaveData): ISaveDataAction {
        return { 
            type: ActionTypes.SaveData, 
            payload: { streamId, data } 
        };
    }
    public static SaveDataSuccess(streamId: string): ISaveDataSuccess {
        return {
            type: ActionTypes.SaveDataSuccess,
            payload: { streamId }
        };
    }
    public static SaveDataFailed(err: string): ISaveDataFailed {
        return {
            type: ActionTypes.SaveDataFailed,
            payload: { errorMessage: err }
        };
    }

    public static DeleteData(streamId: string): IDeleteData {
        return { 
            type: ActionTypes.DeleteData, 
            payload: { streamId } 
        };
    }
    public static DeleteDataSuccess(streamId: string): IDeleteDataSuccess {
        return {
            type: ActionTypes.DeleteDataSuccess,
            payload: { streamId }
        };
    }
    public static DeleteDataFailed(err: string): IDeleteDataFailed {
        return {
            type: ActionTypes.DeleteDataFailed,
            payload: { errorMessage: err }
        };
    }

    public static AnonymizeData(streamId: string): IAnonymizeData {
        return { 
            type: ActionTypes.AnonymizeData, 
            payload: { streamId } 
        };
    }
    public static AnonymizeDataSuccess(streamId: string): IAnonymizeDataSuccess {
        return {
            type: ActionTypes.AnonymizeDataSuccess,
            payload: { streamId }
        };
    }
    public static AnonymizeDataFailed(err: string): IAnonymizeDataFailed {
        return {
            type: ActionTypes.AnonymizeDataFailed,
            payload: { errorMessage: err }
        };
    }

    public static MarkDeceasedData(streamId: string): IMarkDeceasedData {
        return { 
            type: ActionTypes.MarkDeceasedData, 
            payload: { streamId } 
        };
    }
    public static MarkDeceasedDataSuccess(streamId: string): IMarkDeceasedDataSuccess {
        return {
            type: ActionTypes.MarkDeceasedDataSuccess,
            payload: { streamId }
        };
    }
    public static MarkDeceasedDataFailed(err: string): IMarkDeceasedDataFailed {
        return {
            type: ActionTypes.MarkDeceasedDataFailed,
            payload: { errorMessage: err }
        };
    }

    public static UnmarkDeceasedData(streamId: string): IUnmarkDeceasedData {
        return { 
            type: ActionTypes.UnmarkDeceasedData, 
            payload: { streamId } 
        };
    }
    public static UnmarkDeceasedDataSuccess(streamId: string): IUnmarkDeceasedDataSuccess {
        return {
            type: ActionTypes.UnmarkDeceasedDataSuccess,
            payload: { streamId }
        };
    }
    public static UnmarkDeceasedDataFailed(err: string): IUnmarkDeceasedDataFailed {
        return {
            type: ActionTypes.UnmarkDeceasedDataFailed,
            payload: { errorMessage: err }
        };
    }

    public static ResetDeleteState(): IResetDeleteState {
        return { type: ActionTypes.ResetDeleteState };
    }

    public static ResetSaveState(): IResetSaveState {
        return { type: ActionTypes.ResetSaveState };
    }
}

/**********************************************************
 * Reducer
 **********************************************************/
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        case ActionTypes.FetchData:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Busy
                },
                // Reset SaveItem and DeleteItem to clear any previous errors
                SaveItem: {
                    ...InitialState.SaveItem
                },
                DeleteItem: {
                    ...InitialState.DeleteItem
                }
            };
        case ActionTypes.FetchDataSuccess:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.FetchDataFailed:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.SaveData:
            return { 
                ...state, 
                SaveItem: {
                    ...state.SaveItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.SaveDataSuccess:
            return { 
                ...state, 
                SaveItem: {
                    ...InitialState.SaveItem
                },
                Item: {
                    ...InitialState.Item
                }
            };
        case ActionTypes.SaveDataFailed:
            return { 
                ...state, 
                SaveItem: {
                    ...state.SaveItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.DeleteData:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.DeleteDataSuccess:
            return { 
                ...state, 
                DeleteItem: {
                    ...InitialState.DeleteItem
                },
                Item: {
                    ...InitialState.Item
                }
            };
        case ActionTypes.DeleteDataFailed:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.AnonymizeData:
            return { 
                ...state, 
                AnonymizeItem: {
                    ...state.AnonymizeItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.AnonymizeDataSuccess:
            return { 
                ...state, 
                AnonymizeItem: {
                    ...state.AnonymizeItem,
                    state: FetchState.Success,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.AnonymizeDataFailed:
            return { 
                ...state, 
                AnonymizeItem: {
                    ...state.AnonymizeItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.MarkDeceasedData:
            return { 
                ...state, 
                MarkDeceasedItem: {
                    ...state.MarkDeceasedItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.MarkDeceasedDataSuccess:
            return { 
                ...state, 
                MarkDeceasedItem: {
                    ...InitialState.MarkDeceasedItem
                },
                Item: {
                    ...InitialState.Item
                }
            };
        case ActionTypes.MarkDeceasedDataFailed:
            return { 
                ...state, 
                MarkDeceasedItem: {
                    ...state.MarkDeceasedItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.UnmarkDeceasedData:
            return { 
                ...state, 
                UnmarkDeceasedItem: {
                    ...state.UnmarkDeceasedItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.UnmarkDeceasedDataSuccess:
            return { 
                ...state, 
                UnmarkDeceasedItem: {
                    ...InitialState.UnmarkDeceasedItem
                },
                Item: {
                    ...InitialState.Item
                }
            };
        case ActionTypes.UnmarkDeceasedDataFailed:
            return { 
                ...state, 
                UnmarkDeceasedItem: {
                    ...state.UnmarkDeceasedItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.ResetDeleteState:
            return {
                ...state,
                DeleteItem: {
                    ...InitialState.DeleteItem
                }
            };
        case ActionTypes.ResetSaveState:
            return {
                ...state,
                SaveItem: {
                    ...InitialState.SaveItem
                }
            };
        default:
            return state;
    }
}

/**********************************************************
 * Sagas
 **********************************************************/

function* handleIFetchData(action: IFetchData) {
    try {
        const response : Either<any, ICallResult<IData>> = yield call(callApiWithResult, 'GET', `/v1/Client/${action.payload.streamId}`);
        yield response
            .FlatMap(success => put(ActionCreators.FetchDataSuccess(success.result) as any))
            .Reduce(error => put(ActionCreators.FetchDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.FetchDataFailed(JSON.stringify(err)))
    }
}

function* handleISaveData(action: ISaveDataAction) {
    try {
        const response : Either<any, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/Client/${action.payload.streamId}`, action.payload.data);
        yield response
            .FlatMap(() => put(ActionCreators.SaveDataSuccess(action.payload.streamId) as any))
            .Reduce(error => put(ActionCreators.SaveDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.SaveDataFailed(JSON.stringify(err)))
    }
}

function* handleISaveDataSuccess(action: ISaveDataSuccess) {
    yield put(Navigation.ToClientDetail(action.payload.streamId) as any)
}

function* handleIDeleteData(action: IDeleteData) {
    try {
        const response : Either<any, ICallNoResult> = yield call(callApiNoResult, 'DELETE', `/v1/Client/${action.payload.streamId}`);
        yield response
            .FlatMap(() => put(ActionCreators.DeleteDataSuccess(action.payload.streamId) as any))
            .FlatMap(() => put(Navigation.ToClientList() as any))
            .Reduce(error => put(ActionCreators.DeleteDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.DeleteDataFailed(JSON.stringify(err)))
    }
}

function* handleIAnonymizeData(action: IAnonymizeData) {
    try {
        const response : Either<any, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/Client/${action.payload.streamId}/anonymize`);
        yield response
            .FlatMap(() => put(ActionCreators.AnonymizeDataSuccess(action.payload.streamId) as any))
            .FlatMap(() => put(ActionCreators.FetchData(action.payload.streamId) as any))
            .Reduce(error => put(ActionCreators.AnonymizeDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.AnonymizeDataFailed(JSON.stringify(err)))
    }
}

function* handleIMarkDeceasedData(action: IMarkDeceasedData) {
    try {
        const response : Either<any, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/Client/${action.payload.streamId}/markDeceased`);
        yield response
            .FlatMap(() => put(ActionCreators.MarkDeceasedDataSuccess(action.payload.streamId) as any))
            .Reduce(error => put(ActionCreators.MarkDeceasedDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.MarkDeceasedDataFailed(JSON.stringify(err)))
    }
}

function* handleIMarkDeceasedDataSuccess(action: IMarkDeceasedDataSuccess) {
    yield put(Navigation.ToClientDetail(action.payload.streamId) as any)
}

function* handleIUnmarkDeceasedData(action: IUnmarkDeceasedData) {
    try {
        const response : Either<any, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/Client/${action.payload.streamId}/unmarkDeceased`);
        yield response
            .FlatMap(() => put(ActionCreators.UnmarkDeceasedDataSuccess(action.payload.streamId) as any))
            .Reduce(error => put(ActionCreators.UnmarkDeceasedDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.UnmarkDeceasedDataFailed(JSON.stringify(err)))
    }
}

function* handleIUnmarkDeceasedDataSuccess(action: IUnmarkDeceasedDataSuccess) {
    yield put(Navigation.ToClientDetail(action.payload.streamId) as any)
}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchData, handleIFetchData)
    yield takeEvery(ActionTypes.SaveData, handleISaveData)
    yield takeEvery(ActionTypes.SaveDataSuccess, handleISaveDataSuccess)
    yield takeEvery(ActionTypes.DeleteData, handleIDeleteData)
    yield takeEvery(ActionTypes.AnonymizeData, handleIAnonymizeData)
    yield takeEvery(ActionTypes.MarkDeceasedData, handleIMarkDeceasedData)
    yield takeEvery(ActionTypes.MarkDeceasedDataSuccess, handleIMarkDeceasedDataSuccess)
    yield takeEvery(ActionTypes.UnmarkDeceasedData, handleIUnmarkDeceasedData)
    yield takeEvery(ActionTypes.UnmarkDeceasedDataSuccess, handleIUnmarkDeceasedDataSuccess)
}
export function* sagas() {
    yield all([fork(watchAll)]);
}

